import { produce } from 'immer';
import type { ExtractedError } from 'app/lib/error/types';
import { extractError } from 'app/lib/error';
import { ErrorLevel } from 'app/lib/constants';
import { EXECUTE_SQL_QUERY } from 'app/store/snowflakeQueries/types.js';
import { QueryKeys } from 'app/store/snowflakeQueries/reducer.js';
import { UPDATE_SQL_VIEW, CLOSE_SQL_EDITOR, CREATE_SQL_VIEW, PREVIEW_SQL } from './types.js';

export type SqlEditorStateSlice = {
    isSavingSql: boolean;
    keepOpen: boolean;
    error: ExtractedError | null;
};

export const INITIAL_STATE: SqlEditorStateSlice = {
    isSavingSql: false,
    keepOpen: false,
    error: null
};

type ParsedSqlError = ExtractedError & {
    errorPosition?: { line: number; startPosition: number };
    message?: string;
    origins?: string[];
    sql?: string;
};

function parseError(rawMessage: string): ParsedSqlError {
    const typeMatch = rawMessage.match(/^([^{]+)/);
    const _type = typeMatch?.[1]?.trim() ?? 'UnknownError';

    const lineMatch = rawMessage.match(/line=(\d+)/);
    const line = lineMatch?.[1] ? parseInt(lineMatch[1], 10) : undefined;

    const startPositionMatch = rawMessage.match(/startPosition=(\d+)/);
    const startPosition = startPositionMatch?.[1] ? parseInt(startPositionMatch[1], 10) : undefined;

    const messageMatch = rawMessage.match(/message=([^,}]+(?:\{[^}]*\}[^,}]*)*)\./);
    const message = messageMatch?.[1] ? messageMatch[1].trim() + '.' : undefined;

    const originsMatch = rawMessage.match(/origins=\[(.*?)\]/);
    const origins = originsMatch?.[1]
        ? originsMatch[1]
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
        : undefined;

    const sqlMatch = rawMessage.match(/sql=([^}]+)}/);
    const sql = sqlMatch?.[1]?.trim();

    const errorPosition =
        line !== undefined && startPosition !== undefined ? { line, startPosition } : undefined;

    return {
        _type,
        level: ErrorLevel.WARNING,
        errorPosition,
        message,
        origins,
        sql
    };
}

export const sqlEditorReducer = produce((draft, action) => {
    switch (action.type) {
        case CREATE_SQL_VIEW.STARTED:
        case UPDATE_SQL_VIEW.STARTED:
            draft.isSavingSql = true;
            draft.keepOpen = Boolean(action.meta?.keepOpen);
            break;
        case CREATE_SQL_VIEW.FAILED:
            draft.error = parseError(action.payload.failures[0].message);
            break;
        case UPDATE_SQL_VIEW.FAILED:
            draft.error = extractError(action.payload);
            break;
        case CREATE_SQL_VIEW.ENDED:
        case UPDATE_SQL_VIEW.ENDED:
            if (!draft.keepOpen) {
                draft.isSavingSql = false;
            }
            break;
        case PREVIEW_SQL:
        case CLOSE_SQL_EDITOR:
            draft.error = null;
            draft.keepOpen = false;
            break;
        case EXECUTE_SQL_QUERY.ENDED:
            if (action.payload.queryKey === QueryKeys.SQL_PREVIEW) {
                draft.isSavingSql = false;
            }
            break;
        default:
    }
}, INITIAL_STATE);
